import * as React from "react";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Grid, Typography } from "@material-ui/core";

// NPM
import { useTrail, config, interpolate, animated } from "react-spring";

// Utility
import Utility from "../domain/classes/Utility";

// Components
import StyledSectionContainer from "../components/common/SectionContainer.Component";
import PaymentReceivedReminder from "../components/bookingConfirmed/PaymentReceivedReminder.Component";
import ScreeningReminder from "../components/bookingConfirmed/ScreeningReminder.Component";
import TelehealthInstructionsReminder from "../components/bookingConfirmed/TelehealthInstructionsReminder.Component";
import AppointmentDetails from "../components/common/AppointmentDetails.Component";
import CheckinReminder from "../components/bookingConfirmed/CheckinReminder.Component";

// Contexts
import { useApplicationManager } from "../contexts/ApplicationManager.Context";
import IAnimatable from "../domain/interfaces/IAnimatable";

export const styles = (theme: Theme) =>
	createStyles({
		root: {},
		container: { marginTop: theme.spacing(2) },
	});

export interface BookingConfirmedSectionProps extends IAnimatable {}

const BookingConfirmedSection = React.forwardRef<
	HTMLDivElement,
	BookingConfirmedSectionProps & WithStyles<typeof styles>
>(({ classes, hasAnimation }, ref) => {
	const {
		patientDetails,
		stageResponse,
		clearCookies,
	} = useApplicationManager();

	React.useEffect(() => {
		clearCookies();
	}, []);

	const header = (
		<Grid item xs={12}>
			<Typography variant="h2" component="h1" color="primary">
				<strong>Appointment booked!</strong>
			</Typography>
		</Grid>
	);

	const apptDetails = (
		<Grid item xs={12}>
			<AppointmentDetails />
		</Grid>
	);

	const paymentPanel = (
		<Grid item xs={12}>
			<PaymentReceivedReminder />
		</Grid>
	);

	const screeningPanel = (
		<Grid item xs={12}>
			<ScreeningReminder />
		</Grid>
	);

	const telehealthPanel = (
		<Grid item xs={12}>
			<TelehealthInstructionsReminder />
		</Grid>
	);

	const checkInPanel = (
		<Grid item xs={12}>
			<CheckinReminder />
		</Grid>
	);

	const panels = [
		header,
		apptDetails,
		checkInPanel,
		stageResponse.isPrepaid ? paymentPanel : null,
		patientDetails.attendancePreference === "on-site"
			? screeningPanel
			: telehealthPanel,
	];

	const trail = useTrail(panels.length, {
		opacity: 1,
		x: 0,
		config: config.wobbly,
		from: { opacity: 0, x: -20 },
	});

	return (
		<StyledSectionContainer
			ref={ref}
			className={classes.root}
			maxWidth="xs"
		>
			{hasAnimation
				? trail.map(({ opacity, x, ...rest }, index) => {
						const comp = panels[index];

						return (
							<animated.div
								style={{
									opacity,
									transform: interpolate(
										[x],
										(x) => `translateX(${x}px)`
									),
									...rest,
								}}
								className={classes.container}
								key={index}
							>
								{comp}
							</animated.div>
						);
				  })
				: panels.map((comp: JSX.Element | null, index: number) => {
						return (
							<div key={index} className={classes.container}>
								{comp}
							</div>
						);
				  })}
		</StyledSectionContainer>
	);
});

const StyledBookingConfirmedSection = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("BookingConfirmedSection"),
})(BookingConfirmedSection);

export default StyledBookingConfirmedSection;

export const AnimatedBookingConfirmedSection = animated(
	StyledBookingConfirmedSection
);
